import React from 'react';
import { CheckIcon, KeyRoundIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../components/CtaButton';
import { FaqItem } from '../components/FaqItem';
import { bonuses, faqs, features, heroIncludes, offerChecklist, outcomes, painPoints, volumeTitles } from '../data/content';
const EBOOK_COVER_URL = 'https://cdn.magicpatterns.com/uploads/hxHwPrBoY1eq7ZgNm6dDNQ/Mockup_The_Hands_Off_Hosting_System.png';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/1roViPmDXjaiz1sGkRNVUW/Logo_IG.jpg';
const phases = [{
  label: 'Welcome',
  description: 'Answer every guest question before it is asked.'
}, {
  label: 'Protect',
  description: 'House rules and safety that hold up under pressure.'
}, {
  label: 'Impress',
  description: 'Local recommendations that earn written reviews.'
}, {
  label: 'Automate',
  description: 'Self check-in and check-out that run without you.'
}, {
  label: 'Maintain',
  description: 'One written standard for every single turnover.'
}, {
  label: 'Resolve',
  description: 'Emergency scripts so guests solve it first.'
}];
export function SalesPage() {
  return <div className="min-h-screen w-full overflow-x-hidden bg-brand-cream font-body text-slate-800" data-id="element-10" data-mp="35roz5bfkiojf">

      {/* Top banner */}
      <header data-id="element-11" data-mp="2vuu2ky2uuy7s">
        <div className="bg-brand-medium px-4 py-2 text-center text-xs font-bold tracking-wide text-white sm:text-sm" data-id="element-12" data-mp="2vv3uir7gnfg4">
          <span aria-hidden="true" data-id="element-13" data-mp="3tiug5tx8vmvb">🔑 </span>Digital download — Instant access after purchase
        </div>
        
      </header>

      <main id="top" data-id="element-14" data-mp="3ok1322lvdxvo">

        {/* Hero */}
        <section className="bg-brand-deep px-5 py-8 text-center text-white sm:py-14 lg:py-16" data-id="element-15" data-mp="3jk4wpwpp9ucy">
          <div className="mx-auto max-w-4xl" data-id="element-16" data-mp="3fj35o35xsom2">
            <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl" data-id="element-17" data-mp="3fi1kh5v7ilqm">
              Never Handle a Guest{' '}
              <span className="text-brand-gold" data-id="element-18" data-mp="243wanw8bbhnd">Emergency Again</span>
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-white/85 sm:mt-4 sm:text-xl" data-id="element-19" data-mp="25ktncd9klr93">
              95% of issues resolve automatically. Your property stays 5-star. You stay asleep.{' '}
              <strong className="font-black text-white" data-id="element-20" data-mp="1vmvybnjvzqff">The Hands-Off Hosting System</strong> makes it all possible.
            </p>
            <img src={EBOOK_COVER_URL} alt="The complete Hands-Off Hosting System — six volumes plus 5 bonuses included" className="mx-auto mt-3 w-full max-w-[280px] bg-transparent object-contain sm:mt-5 sm:max-w-md lg:max-w-lg" data-id="element-21" data-mp="20kp269xpw8cj" />
            <div className="mt-4 sm:mt-6" data-id="element-22" data-mp="1rn52h5fi6k41">
              <CtaButton label="YES, I WANT HANDS-OFF HOSTING!" data-id="element-23" data-mp="1cwo11m2vbu75" />
            </div>

            {/* What you get */}
            <div className="mx-auto mt-8 max-w-2xl text-left sm:mt-10" data-id="element-24" data-mp="1hwaftzychii2">
              <p className="text-center text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-25" data-mp="13vpxt6097qpm">
                Here is everything you get
              </p>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2" role="list" data-id="element-26" data-mp="17z5ozmtk0yux">
                {heroIncludes.map(item => <li key={item} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-id="element-27" data-mp="pavanu37jxbp">
                    <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-gold" aria-hidden="true" data-id="element-28" data-mp="y978oh6elxe6" />
                    <span className="text-[15px] leading-snug text-white/90" data-id="element-29" data-mp="tam9ml2w1jrb">{item}</span>
                  </li>)}
              </ul>

              <p className="mt-7 text-center text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-30" data-mp="186bwsmjt82xm">
                The 6 volumes inside
              </p>
              <ul className="mt-4 space-y-2" role="list" data-id="element-31" data-mp="1gzox49k7e6tq">
                {volumeTitles.map(volume => <li key={volume} className="flex items-start gap-3 border-l-2 border-brand-gold/60 pl-4 text-[15px] leading-snug text-white/90" data-id="element-32" data-mp="1cybpbtxjvtk6">
                    {volume}
                  </li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* Social proof bar */}
        <section aria-label="Social proof" className="bg-[#EAE2D0] px-5 py-4 text-center text-sm font-bold sm:text-base text-brand-deep" data-id="element-33" data-mp="2614eqa5usmk3">
          <span aria-hidden="true" data-id="element-34" data-mp="2a1odfi62ry8c">🏡 </span>Built for short-term rental hosts who are done being on call 24/7
        </section>

        {/* Pain points */}
        <section id="pain-points" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-35" data-mp="2jsjazioruk93">
          <div className="mx-auto max-w-5xl" data-id="element-36" data-mp="2junazpjjuz43">
            <div className="mx-auto max-w-2xl text-center" data-id="element-37" data-mp="1n31yokva9zyf">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-38" data-mp="1n4msu9dorgvr">
                You are not alone
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-39" data-mp="1w1x1ro316txi">
                Does any of this sound familiar?
              </h2>
            </div>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-5" role="list" data-id="element-40" data-mp="37h27z22dfalv">
              {painPoints.map(point => <li key={point.text} className="flex items-start gap-4 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-41" data-mp="3cg5aofuvp4qo">
                  <span className="mt-0.5 text-2xl" aria-hidden="true" data-id="element-42" data-mp="10vxwlb8rrwwa">{point.emoji}</span>
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-43" data-mp="vwv04f6uuyzx">{point.text}</span>
                </li>)}
            </ul>
            <blockquote className="mx-auto mt-10 max-w-3xl border-l-4 border-brand-medium pl-5 font-display text-xl font-bold italic leading-relaxed text-brand-deep sm:text-2xl" data-id="element-44" data-mp="m58edoww5hix">
              "You do not have a hosting problem. You have a missing operating system for your property."
            </blockquote>
            <p className="mx-auto mt-5 max-w-3xl text-center text-base leading-relaxed text-slate-600" data-id="element-45" data-mp="84nxiyajdvs2">
              Most hosts answer the same twenty questions for every booking, explain check-in from scratch each time, and rely on memory for turnovers. That is not hospitality — it is unpaid admin. Documented systems are what separate a hands-off property from a second job.
            </p>
          </div>
        </section>

        {/* Solution */}
        <section id="solution" className="bg-brand-deep px-5 py-12 text-white sm:py-16" data-id="element-46" data-mp="3rz13ob3v6yc6">
          <div className="mx-auto max-w-3xl text-center" data-id="element-47" data-mp="mzhirclz9za">
            <KeyRoundIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-48" data-mp="3i2xu4k6onxf7" />
            <p className="mt-5 text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-49" data-mp="3n0pywesffe0t">The solution</p>
            <h2 className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl" data-id="element-50" data-mp="el68ron1ybpz">
              You don&apos;t need to work harder. You need a documented system.
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-white/85" data-id="element-51" data-mp="5jpwtcgla1jn">
              <p data-id="element-52" data-mp="9ltjyycmeukr">
                The Hands-Off Hosting System is not another list of hosting tips. It is a complete 6-volume operating manual for your property: the guides guests read, the rules that protect you, and the standards your cleaner follows.
              </p>
              <p data-id="element-53" data-mp="2ua91ioeapc7v">
                <strong className="font-bold text-white" data-id="element-54" data-mp="2mst0cew1w5sx">Set it up once</strong> and every future guest gets the same seamless stay — without you answering another midnight message.
              </p>
            </div>
            <div className="mt-8 grid gap-3 text-left sm:grid-cols-2" data-id="element-55" data-mp="2wriim8ruzs9p">
              {phases.map(phase => <div key={phase.label} className="rounded-xl border border-white/15 bg-white/5 p-4" data-id="element-56" data-mp="2rrm95u0e78mr">
                  <p className="text-sm font-black uppercase tracking-wide text-brand-gold" data-id="element-57" data-mp="241s0zpr0q88u">{phase.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/80" data-id="element-58" data-mp="1z4jgvjmb7u4y">{phase.description}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* What's inside */}
        <section id="inside" className="bg-[#E7E0D1] px-5 py-12 sm:py-16" data-id="element-59" data-mp="290msrh6yr753">
          <div className="mx-auto max-w-6xl" data-id="element-60" data-mp="1lbk1riqfw1xx">
            <div className="mx-auto max-w-2xl text-center" data-id="element-61" data-mp="1laimt36ayvax">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-62" data-mp="1gdsb0s1j6lvb">
                Fill-in-the-blank templates, ready to use
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-63" data-mp="zsqyluanwd2p">
                The 6 Volumes of the System
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-64" data-mp="14u7vzftxthej">
              {features.map((feature, index) => <article key={feature.title} className="rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-65" data-mp="1dsjjva3f613a" data-mpi={index}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-medium font-display text-lg font-bold text-white" data-id="element-66" data-mp="1iquuvo5gahn3">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-67" data-mp="1sgmr0mt77ito">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-xs font-black uppercase tracking-wide text-brand-medium" data-id="element-68" data-mp="21hcl9m2t915a">
                    {feature.pain}
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-69" data-mp="3lxsgdrhqmiyo">
                    {feature.description}
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section id="bonuses" className="bg-[#F1EADB] px-5 py-12 sm:py-16" data-id="element-70" data-mp="3815lrvd9c4vx">
          <div className="mx-auto max-w-5xl" data-id="element-71" data-mp="8l0htsh0oqzs">
            <div className="mx-auto max-w-2xl text-center" data-id="element-72" data-mp="8kqsm73sqnv5">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-73" data-mp="4j58q1suh9bg">
                Included at no extra cost
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-74" data-mp="3vw88nxsi3y9r">
                🎁 5 Free Bonuses Included
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-75" data-mp="sa15ldc5qupk">
                Everything you need to hand your property over to a system today.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-76" data-mp="31fazxe941iw9">
              {bonuses.map(bonus => <article key={bonus.title} className="rounded-r-2xl border-l-4 border-brand-medium bg-white px-5 py-5 shadow-sm sm:px-6" data-id="element-77" data-mp="2wfyemxxw0jvs">
                  <div className="flex items-start gap-4" data-id="element-78" data-mp="3ag97g6irc8jl">
                    <span className="text-3xl" aria-hidden="true" data-id="element-79" data-mp="36d3354f7grfr">{bonus.emoji}</span>
                    <div data-id="element-80" data-mp="3kcua4l7b17k9">
                      <span className="inline-block rounded-full bg-brand-gold px-3 py-1 text-xs font-black uppercase tracking-wide text-brand-deep" data-id="element-81" data-mp="3kc2i1kcx1ryq">
                        {bonus.tag}
                      </span>
                      <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-82" data-mp="3u2dww1y51lke">
                        {bonus.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-83" data-mp="6xfxs4t3o3up">
                        {bonus.description}
                      </p>
                    </div>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        {/* Author */}
        <section id="author" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-84" data-mp="qk9ddvkcl9qs">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-7 text-center md:flex-row md:text-left" data-id="element-85" data-mp="cki1q1hb05fy">
            <img src={LOGO_URL} alt="Prof. Kyra logo" className="h-40 w-40 flex-none rounded-full border-8 border-white bg-black object-contain shadow-lg" data-id="element-86" data-mp="gl217bpcnl59" />
            <div data-id="element-87" data-mp="2slrxt5uuqvw">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-88" data-mp="7kvecz7rh8tf">
                Created with care
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-89" data-mp="3p2y7c9gx8nqi">
                Meet Prof. Kyra
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-700" data-id="element-90" data-mp="3g0f43o98493q">
                Prof. Kyra creates practical, field-tested digital guides designed to solve real everyday problems. Every resource is built to give real tools — not just information.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-91" data-mp="1qlnugut23sls">
                The Hands-Off Hosting System was created for hosts who want a calm, professional operation instead of a phone that never stops buzzing.
              </p>
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section id="outcomes" className="bg-[#E7E0D1] px-5 py-12 sm:py-16" data-id="element-92" data-mp="29ifg3aw2mjt0">
          <div className="mx-auto max-w-4xl text-center" data-id="element-93" data-mp="29gbb0pmfrer5">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-94" data-mp="24kvyq242tqwu">
              What you will achieve
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-95" data-mp="1viwivd4sssvh">
              After the System
            </h2>
            <p className="mx-auto mt-5 max-w-3xl font-display text-xl font-bold italic leading-relaxed text-brand-deep" data-id="element-96" data-mp="p4f24ge1gkz8">
              "A great stay is not improvised. It is the result of a system written down once."
            </p>
            <ul className="mt-8 grid gap-4 text-left sm:grid-cols-2" role="list" data-id="element-97" data-mp="2vp9puqa2i3ma">
              {outcomes.map(outcome => <li key={outcome} className="flex items-start gap-3 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-98" data-mp="34rsrj724aa6y">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-olive" aria-hidden="true" data-id="element-99" data-mp="39jj4lksfvc22" />
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-100" data-mp="3egj1b53ef2gi">{outcome}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Offer */}
        <section id="offer" className="bg-brand-deep px-5 py-12 sm:py-16" data-id="element-101" data-mp="3ofi9yno5yy3i">
          <div className="mx-auto max-w-xl rounded-3xl bg-white p-7 text-center shadow-2xl sm:p-8" data-id="element-102" data-mp="3shdd7xpnnljx">
            <SparklesIcon className="mx-auto h-9 w-9 text-brand-medium" aria-hidden="true" data-id="element-103" data-mp="1u20jarsgpl49" />
            <h2 className="mt-4 font-display text-3xl font-bold text-brand-deep sm:text-4xl" data-id="element-104" data-mp="1y3cdzbq70wjb">
              Everything You Get Today
            </h2>
            <p className="mt-2 font-display text-2xl font-bold text-brand-deep sm:text-3xl" data-id="element-105" data-mp="12run0sg692ix">
              The Hands-Off Hosting System
            </p>
            <ul className="mt-7 space-y-3 text-left" role="list" data-id="element-106" data-mp="1crbvfhmt5uil">
              {offerChecklist.map(item => <li key={item} className="flex items-start gap-3 text-[15px] leading-snug text-slate-700" data-id="element-107" data-mp="17s8wmexf8kj2">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-olive" aria-hidden="true" data-id="element-108" data-mp="k2em440kr81z" />
                  <span data-id="element-109" data-mp="f3uvvrey7a3t">{item}</span>
                </li>)}
            </ul>
            <div className="mt-8 flex flex-col items-center border-t border-brand-medium/15 pt-7 text-center" data-id="element-110" data-mp="f1qk6nat56sz">
              <p className="text-base font-bold text-slate-500 line-through" data-id="element-111" data-mp="p1ra4s36ml65">67 USD</p>
              <p className="mt-1 font-display text-5xl font-bold leading-none text-brand-deep" data-id="element-112" data-mp="ozxzz2td1ite">
                27 <span className="align-middle text-2xl" data-id="element-113" data-mp="19txwzvtlr4i">USD</span>
              </p>
              <p className="mt-2 text-sm font-bold text-slate-600" data-id="element-114" data-mp="3si7u1ugubb7f">
                One-time payment — Yours forever
              </p>
            </div>
            <p className="mt-6 text-sm font-black text-brand-medium" data-id="element-115" data-mp="2crfaalhpzdol">
              Launch offer · Price goes up soon
            </p>
            <CtaButton label="YES, I WANT HANDS-OFF HOSTING!" className="mt-3 w-full" target="checkout" data-id="element-116" data-mp="1z1buoddcii84" />
            <p className="mt-4 text-xs font-bold tracking-wide text-slate-500" data-id="element-117" data-mp="231lzvntbd4lx">
              🔒 Secure checkout <span aria-hidden="true" data-id="element-118" data-mp="2qrq0dmb7go8p">|</span> 📩 Instant delivery <span aria-hidden="true" data-id="element-119" data-mp="2vqjdveqhsf8v">|</span> 🛡️ 7-day guarantee
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-120" data-mp="2hpqi6qogfe46">
          <div className="mx-auto max-w-3xl" data-id="element-121" data-mp="2moa78zych60r">
            <div className="text-center" data-id="element-122" data-mp="2mrfu08ji3vtb">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-123" data-mp="16atsdekg4kvr">
                Clear answers
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-124" data-mp="1bbhizz4rizpg">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="mt-8 space-y-3" data-id="element-125" data-mp="380smijvlb83l">
              {faqs.map(faq => <FaqItem key={faq.question} {...faq} data-id="element-126" data-mp="338r2ucw3ef3j" />)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="final-cta" className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16" data-id="element-127" data-mp="2z7fleundh06f">
          <div className="mx-auto max-w-3xl" data-id="element-128" data-mp="2uafe6gesdrdq">
            <ZapIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-129" data-mp="2pa13eqlsw3c0" />
            <h2 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-id="element-130" data-mp="2kaol8j5vjkfr">
              Hand the work to a system. Host hands-off.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85" data-id="element-131" data-mp="2gaw8du9g6s4l">
              If you set the system up and it does not reduce the guest messages, check-in calls, and turnover chaos in your hosting week, we will refund 100% of your investment. No questions. No hassle.
            </p>
            <p className="mt-5 text-sm font-black text-brand-gold" data-id="element-132" data-mp="1wd8rfs89gh6i">
              Now with the full 6×1 system!
            </p>
            <CtaButton label="YES, I WANT HANDS-OFF HOSTING!" className="mt-3" data-id="element-133" data-mp="265egou74oki0" />
          </div>
        </section>

      </main>

      <footer className="bg-[#0C1729] px-5 py-10 text-center text-white/70" data-id="element-134" data-mp="2a6zssdbu8ur5">
        <ShieldCheckIcon className="mx-auto h-6 w-6 text-brand-gold" aria-hidden="true" data-id="element-135" data-mp="1dgqtgt2tjl1t" />
        <p className="mt-4 text-sm" data-id="element-136" data-mp="1ifk1w7390lv4">© 2026 Prof. Kyra · profkyra@gmail.com</p>
        <nav aria-label="Footer navigation" className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm" data-id="element-137" data-mp="1ihoa3186k717">
          <a href="#privacy" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-138" data-mp="1rg8kzg7i1cev">
            Privacy Policy
          </a>
          <a href="#terms" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-139" data-mp="32v5a0q3oo1m8">
            Terms of Service
          </a>
          <a href="mailto:profkyra@gmail.com" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-140" data-mp="29k0egji8032h">
            Contact
          </a>
        </nav>
        <p className="mx-auto mt-6 max-w-3xl text-xs leading-relaxed text-white/55" data-id="element-141" data-mp="2ijdr1l1n57rf">
          This product is an educational and informational resource for property hosts. It does not constitute legal, tax, or insurance advice.
        </p>
      </footer>

    </div>;
}